import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

type LayoutStyle = 'grid2x2' | 'listcard' | 'bigfeatured' | 'magazine' | 'swiperow' | 'spotlight' | 'duosplit' | 'staggered' | 'fullstack';

interface Props {
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

const LAYOUTS: {
  id: LayoutStyle;
  name: string;
  desc: string;
  icon: string;
  preview: string;
}[] = [
  {
    id: 'grid2x2',
    name: 'Classic Grid',
    desc: '2x2 square cards — clean aur balanced look',
    icon: 'ri-grid-line',
    preview: 'grid2x2',
  },
  {
    id: 'listcard',
    name: 'List Cards',
    desc: 'Horizontal cards with image + details side by side',
    icon: 'ri-list-check-2',
    preview: 'listcard',
  },
  {
    id: 'bigfeatured',
    name: 'Big Featured',
    desc: '1 large hero card + 2x2 small cards below',
    icon: 'ri-layout-top-2-line',
    preview: 'bigfeatured',
  },
  {
    id: 'magazine',
    name: 'Magazine',
    desc: 'Asymmetric editorial layout — premium feel',
    icon: 'ri-layout-masonry-line',
    preview: 'magazine',
  },
  {
    id: 'swiperow',
    name: 'Swipe Row',
    desc: 'Horizontal scroll cards — modern app style',
    icon: 'ri-swap-line',
    preview: 'swiperow',
  },
  {
    id: 'spotlight',
    name: 'Spotlight',
    desc: '1 full-width big hero + small scroll strip neeche',
    icon: 'ri-focus-3-line',
    preview: 'spotlight',
  },
  {
    id: 'duosplit',
    name: 'Duo Split',
    desc: '2 tall cards upar + 3 small cards neeche',
    icon: 'ri-layout-column-line',
    preview: 'duosplit',
  },
  {
    id: 'staggered',
    name: 'Staggered Tiles',
    desc: 'Pinterest-style unequal height cards — trendy look',
    icon: 'ri-layout-grid-2-line',
    preview: 'staggered',
  },
  {
    id: 'fullstack',
    name: 'Full Width Stack',
    desc: 'Bold full-width stacked cards — strong visual impact',
    icon: 'ri-stack-line',
    preview: 'fullstack',
  },
];

// Mini preview thumbnails for each layout
function LayoutPreview({ id }: { id: LayoutStyle }) {
  if (id === 'grid2x2') return (
    <div className="grid grid-cols-2 gap-1 w-full h-full p-1">
      {[0,1,2,3].map(i => <div key={i} className="rounded bg-gray-300 dark:bg-slate-600" />)}
    </div>
  );
  if (id === 'listcard') return (
    <div className="flex flex-col gap-1 w-full h-full p-1">
      {[0,1,2].map(i => (
        <div key={i} className="flex gap-1 flex-1">
          <div className="w-8 rounded bg-gray-300 dark:bg-slate-600" />
          <div className="flex-1 rounded bg-gray-200 dark:bg-slate-700" />
        </div>
      ))}
    </div>
  );
  if (id === 'bigfeatured') return (
    <div className="flex flex-col gap-1 w-full h-full p-1">
      <div className="flex-1 rounded bg-gray-300 dark:bg-slate-600" />
      <div className="grid grid-cols-2 gap-1" style={{ height: '40%' }}>
        <div className="rounded bg-gray-300 dark:bg-slate-600" />
        <div className="rounded bg-gray-300 dark:bg-slate-600" />
      </div>
    </div>
  );
  if (id === 'magazine') return (
    <div className="flex flex-col gap-1 w-full h-full p-1">
      <div className="flex gap-1" style={{ height: '60%' }}>
        <div className="flex-1 rounded bg-gray-300 dark:bg-slate-600" />
        <div className="flex flex-col gap-1" style={{ width: '40%' }}>
          <div className="flex-1 rounded bg-gray-300 dark:bg-slate-600" />
          <div className="flex-1 rounded bg-gray-300 dark:bg-slate-600" />
        </div>
      </div>
      <div className="rounded bg-gray-300 dark:bg-slate-600" style={{ height: '35%' }} />
    </div>
  );
  if (id === 'swiperow') return (
    <div className="flex gap-1 w-full h-full p-1 overflow-hidden">
      {[0,1,2,3].map(i => (
        <div key={i} className="shrink-0 rounded bg-gray-300 dark:bg-slate-600" style={{ width: '35%' }} />
      ))}
    </div>
  );
  if (id === 'spotlight') return (
    <div className="flex flex-col gap-1 w-full h-full p-1">
      <div className="rounded bg-gray-300 dark:bg-slate-600" style={{ height: '65%' }} />
      <div className="flex gap-1" style={{ height: '30%' }}>
        {[0,1,2].map(i => (
          <div key={i} className="flex-1 rounded bg-gray-300 dark:bg-slate-600" />
        ))}
      </div>
    </div>
  );
  if (id === 'duosplit') return (
    <div className="flex flex-col gap-1 w-full h-full p-1">
      <div className="grid grid-cols-2 gap-1" style={{ height: '55%' }}>
        <div className="rounded bg-gray-300 dark:bg-slate-600" />
        <div className="rounded bg-gray-300 dark:bg-slate-600" />
      </div>
      <div className="grid grid-cols-3 gap-1" style={{ height: '40%' }}>
        <div className="rounded bg-gray-300 dark:bg-slate-600" />
        <div className="rounded bg-gray-300 dark:bg-slate-600" />
        <div className="rounded bg-gray-300 dark:bg-slate-600" />
      </div>
    </div>
  );
  if (id === 'staggered') return (
    <div className="flex gap-1 w-full h-full p-1">
      <div className="flex-1 flex flex-col gap-1">
        <div className="rounded bg-gray-300 dark:bg-slate-600" style={{ height: '55%' }} />
        <div className="rounded bg-gray-300 dark:bg-slate-600" style={{ height: '40%' }} />
      </div>
      <div className="flex-1 flex flex-col gap-1 pt-3">
        <div className="rounded bg-gray-300 dark:bg-slate-600" style={{ height: '40%' }} />
        <div className="rounded bg-gray-300 dark:bg-slate-600" style={{ height: '55%' }} />
      </div>
    </div>
  );
  if (id === 'fullstack') return (
    <div className="flex flex-col gap-1 w-full h-full p-1">
      <div className="rounded bg-gray-300 dark:bg-slate-600" style={{ height: '40%' }} />
      <div className="rounded bg-gray-200 dark:bg-slate-700" style={{ height: '25%' }} />
      <div className="rounded bg-gray-200 dark:bg-slate-700" style={{ height: '25%' }} />
    </div>
  );
  return null;
}

export default function CollectionLayoutPanel({ onToast }: Props) {
  const [currentLayout, setCurrentLayout] = useState<LayoutStyle>('grid2x2');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('collection_settings').select('layout_style').eq('id', 'main').maybeSingle();
      if (data?.layout_style) setCurrentLayout(data.layout_style as LayoutStyle);
      setLoaded(true);
    };
    load();
  }, []);

  const handleApprove = async (layoutId: LayoutStyle) => {
    setSaving(true);
    const { error } = await supabase
      .from('collection_settings')
      .upsert({ id: 'main', layout_style: layoutId, updated_at: new Date().toISOString() });
    if (error) {
      onToast('Layout save nahi hua: ' + error.message, 'error');
    } else {
      setCurrentLayout(layoutId);
      onToast(`"${LAYOUTS.find(l => l.id === layoutId)?.name}" layout approve ho gaya! Home page pe live hai.`, 'success');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
            <i className="ri-layout-4-line text-orange-600 text-lg"></i>
          </div>
          <div>
            <p className="text-gray-900 font-bold text-base">Collection Layout Selector</p>
            <p className="text-gray-400 text-xs">Home page ke "Our Collection" section ka design choose karo</p>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 flex items-center justify-center shrink-0">
          <i className="ri-lightbulb-flash-line text-amber-500 text-lg"></i>
        </div>
        <div>
          <p className="text-amber-800 font-bold text-sm">Kaise kaam karta hai?</p>
          <p className="text-amber-700 text-xs mt-0.5 leading-relaxed">
            Neeche 5 alag designs hain. Jo design pasand aaye uska <strong>"Approve & Apply"</strong> button dabao — home page pe turant woh design dikhne lagega!
          </p>
        </div>
      </div>

      {/* Layout Cards */}
      {!loaded ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-orange-500 animate-spin"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {LAYOUTS.map((layout, idx) => {
            const isActive = currentLayout === layout.id;
            return (
              <div
                key={layout.id}
                className={`bg-white rounded-2xl border-2 overflow-hidden transition-all ${isActive ? 'border-orange-500' : 'border-gray-100'}`}
              >
                <div className="flex items-stretch gap-0">
                  {/* Preview thumbnail */}
                  <div
                    className={`shrink-0 flex items-center justify-center ${isActive ? 'bg-orange-50' : 'bg-gray-50'}`}
                    style={{ width: 110, height: 110 }}
                  >
                    <div className="w-20 h-20">
                      <LayoutPreview id={layout.id} />
                    </div>
                  </div>

                  {/* Info + action */}
                  <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isActive ? 'bg-orange-500' : 'bg-gray-100'}`}>
                          <i className={`${layout.icon} text-sm ${isActive ? 'text-white' : 'text-gray-500'}`}></i>
                        </div>
                        <p className="text-gray-900 font-black text-sm">{layout.name}</p>
                        <span className="text-gray-400 text-[10px] font-bold bg-gray-100 px-2 py-0.5 rounded-full">Design {idx + 1}</span>
                        {isActive && (
                          <span className="text-orange-600 text-[10px] font-black bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <i className="ri-checkbox-circle-fill"></i> LIVE
                          </span>
                        )}
                      </div>
                      <p className="text-gray-500 text-xs mt-1.5 leading-relaxed">{layout.desc}</p>
                    </div>

                    <button
                      onClick={() => handleApprove(layout.id)}
                      disabled={saving || isActive}
                      className={`mt-3 w-full py-2.5 rounded-xl text-xs font-black flex items-center justify-center gap-1.5 transition-all cursor-pointer whitespace-nowrap disabled:cursor-default ${
                        isActive
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-900 text-white hover:bg-gray-700'
                      } disabled:opacity-70`}
                    >
                      {saving ? (
                        <><i className="ri-loader-4-line animate-spin"></i>Applying...</>
                      ) : isActive ? (
                        <><i className="ri-checkbox-circle-fill"></i>Currently Active</>
                      ) : (
                        <><i className="ri-check-double-line"></i>Approve &amp; Apply</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Current status */}
      <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-green-500 flex items-center justify-center shrink-0">
          <i className="ri-live-line text-white"></i>
        </div>
        <div>
          <p className="text-green-800 font-bold text-sm">Currently Live</p>
          <p className="text-green-700 text-xs">
            <strong>{LAYOUTS.find(l => l.id === currentLayout)?.name}</strong> — home page pe yeh design dikh raha hai
          </p>
        </div>
      </div>
    </div>
  );
}
